import { createQueryKeys } from '@lukemorales/query-key-factory';
import type {
  GetProposalGoodsServiceRequest,
  GetProposalServiceRequest,
  GetProposalsServiceRequest,
  GetRecruitmentCampaignsServiceRequest,
  GetReverseProposalBrandsServiceRequest,
  GetReverseProposalGoodsDetailServiceRequest,
  GetReverseProposalServiceRequest,
  GetReverseProposalsServiceRequest,
  GetSeedingMeProposalServiceRequest,
  GetSeedingMeRecruitmentServiceRequest,
  GetSeedingProductOptionsServiceRequest,
  GetSeedingProposalDetailServiceRequest,
  GetSeedingRecruitmentDetailServiceRequest,
  SearchReverseProposalGoodsServiceRequest,
} from '@musinsa/services-curator';
import {
  getInProgressCampaignsService,
  getProposalGoodsService,
  getProposalService,
  getProposalsService,
  getRecruitmentCampaignCategoriesService,
  getRecruitmentCampaignsService,
  getReverseProposalBrandsService,
  getReverseProposalGoodsDetailService,
  getReverseProposalService,
  getReverseProposalsService,
  getSeedingCampaignDetailService,
  getSeedingDeliveryAddressesService,
  getSeedingMeProposalCountService,
  getSeedingMeProposalService,
  getSeedingMeRecruitmentCountService,
  getSeedingMeRecruitmentService,
  getSeedingProductOptionsService,
  getSeedingProposalDetailService,
  getSeedingProposalRejectionReasonsService,
  getSeedingRecruitmentDetailService,
  getSeedingRejectionReasonsService,
  searchReverseProposalGoodsService,
} from '@musinsa/services-curator';

export const campaignsFactory = createQueryKeys('campaigns', {
  inProgress: () => ({
    queryKey: ['inProgress'],
    queryFn: getInProgressCampaignsService,
  }),
  proposals: (params: Omit<GetProposalsServiceRequest, 'page'>) => ({
    queryKey: [{ params }],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      getProposalsService({ ...params, page: pageParam }),
  }),
  proposalDetail: (params: GetProposalServiceRequest) => ({
    queryKey: [params.campaignProposalId],
    queryFn: () => getProposalService(params),
  }),
  proposalGoods: (params: Omit<GetProposalGoodsServiceRequest, 'page'>) => ({
    queryKey: [params.campaignProposalId],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      getProposalGoodsService({ ...params, page: pageParam }),
  }),
  reverseProposals: (params: Omit<GetReverseProposalsServiceRequest, 'page'>) => ({
    queryKey: [{ params }],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      getReverseProposalsService({ ...params, page: pageParam }),
  }),
  reverseProposalDetail: (params: GetReverseProposalServiceRequest) => ({
    queryKey: [params.reverseProposalId],
    queryFn: () => getReverseProposalService(params),
  }),
  reverseProposalGoods: (params: Omit<SearchReverseProposalGoodsServiceRequest, 'page'>) => ({
    queryKey: [{ params }],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      searchReverseProposalGoodsService({ ...params, page: pageParam }),
  }),
  reverseProposalGoodsDetail: (params: GetReverseProposalGoodsDetailServiceRequest) => ({
    queryKey: [params.goodsNo],
    queryFn: () => getReverseProposalGoodsDetailService(params),
  }),
  reverseProposalBrands: (params: Omit<GetReverseProposalBrandsServiceRequest, 'page'>) => ({
    queryKey: [{ params }],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      getReverseProposalBrandsService({ ...params, page: pageParam }),
  }),
  recruitment: (params: Omit<GetRecruitmentCampaignsServiceRequest, 'page'>) => ({
    queryKey: [{ params }],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      getRecruitmentCampaignsService({ ...params, page: pageParam }),
  }),
  recruitmentCategories: () => ({
    queryKey: ['recruitmentCategories'],
    queryFn: () => getRecruitmentCampaignCategoriesService(),
  }),
  seedingMeRecruitment: (params: Omit<GetSeedingMeRecruitmentServiceRequest, 'page'>) => ({
    queryKey: [{ params }],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      getSeedingMeRecruitmentService({ ...params, page: pageParam }),
  }),
  seedingMeRecruitmentCount: () => ({
    queryKey: ['seedingMeRecruitmentCount'],
    queryFn: () => getSeedingMeRecruitmentCountService(),
  }),
  seedingMeProposal: (params: Omit<GetSeedingMeProposalServiceRequest, 'page'>) => ({
    queryKey: [{ params }],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      getSeedingMeProposalService({ ...params, page: pageParam }),
  }),
  seedingMeProposalCount: () => ({
    queryKey: ['seedingMeProposalCount'],
    queryFn: () => getSeedingMeProposalCountService(),
  }),
  seedingMeProposalLatestPending: () => ({
    queryKey: ['seedingMeProposalLatestPending'],
    queryFn: () => getSeedingMeProposalService({ searchStatus: 'PENDING', page: 1, size: 1 }),
  }),
  // 내가 신청한(모집형) 최신 PENDING 1건 — blue dot 미확인 판정용. (SID-4312, 제안형과 동일 패턴)
  seedingMeRecruitmentLatestPending: () => ({
    queryKey: ['seedingMeRecruitmentLatestPending'],
    queryFn: () => getSeedingMeRecruitmentService({ searchStatus: 'PENDING', page: 1, size: 1 }),
  }),
  seedingProposalRejectionReasons: () => ({
    queryKey: ['seedingProposalRejectionReasons'],
    queryFn: () => getSeedingProposalRejectionReasonsService(),
  }),
  seedingRecruitmentDetail: (params: GetSeedingRecruitmentDetailServiceRequest) => ({
    queryKey: [{ kind: 'recruitment', campaignId: params.campaignId }],
    queryFn: () => getSeedingRecruitmentDetailService(params),
  }),
  seedingProposalDetail: (params: GetSeedingProposalDetailServiceRequest) => ({
    queryKey: [{ kind: 'proposal', campaignId: params.campaignId }],
    queryFn: () => getSeedingProposalDetailService(params),
  }),
  // 통일 상세 — campaignId 만으로 모집/제안 분기 (참여-후 단일 라우트용).
  seedingCampaignDetail: (params: { campaignId: number }) => ({
    queryKey: [{ kind: 'campaign', campaignId: params.campaignId }],
    queryFn: () => getSeedingCampaignDetailService(params.campaignId),
  }),
  // 29CM 배송지 변경 — 큐레이터 배송지 목록
  seedingDeliveryAddresses: () => ({
    queryKey: ['seedingDeliveryAddresses'],
    queryFn: () => getSeedingDeliveryAddressesService(),
  }),
  seedingRejectionReasons: () => ({
    queryKey: ['rejectionReasons'],
    queryFn: () => getSeedingRejectionReasonsService(),
  }),
  seedingProductOptions: (params: GetSeedingProductOptionsServiceRequest) => ({
    queryKey: [{ campaignId: params.campaignId, goodsNos: params.goodsNos }],
    queryFn: () => getSeedingProductOptionsService(params),
  }),
});
